import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tabs } from '../components/ui/Tabs';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { EditStoreDetailsModal } from '../components/store/EditStoreDetailsModal';
import { DuplicateStoreModal } from '../components/store/DuplicateStoreModal';
import { ForceCloseStoreModal } from '../components/store/ForceCloseStoreModal';
import { mockStores } from '../utils/mockData';
import { calculateStoreStatus, getStatusColor } from '../utils/storeStatus';
import { ExternalLink, ArrowLeft, Copy, Check } from 'lucide-react';
import { WizardStep2Schedule } from '../components/wizard/WizardStep2Schedule';
import { WizardStep4Products } from '../components/wizard/WizardStep4Products';
import { useToast } from '../hooks/useToast';
export function PopUpStoreDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const store = mockStores.find(s => s.id === id) || mockStores[0];
  const [activeTab, setActiveTab] = useState('overview');
  const [previewState, setPreviewState] = useState<string>('actual');
  const [copied, setCopied] = useState(false);
  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isForceCloseModalOpen, setIsForceCloseModalOpen] = useState(false);
  const status = calculateStoreStatus(store);
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'schedule',
    label: 'Schedule & Publish'
  }, {
    id: 'products',
    label: 'Products'
  }, {
    id: 'orders',
    label: 'Orders'
  }, {
    id: 'fulfillment',
    label: 'Fulfillment'
  }, {
    id: 'settings',
    label: 'Settings'
  }];
  const storefrontUrl = `https://stores.printer.com/store/${store.slug}`;
  const getPreviewUrl = () => {
    const baseUrl = `/store/${store.slug}?preview=1`;
    if (previewState === 'actual') {
      return baseUrl;
    }
    return `${baseUrl}&state=${previewState}`;
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(storefrontUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handlePreview = () => {
    window.open(getPreviewUrl(), '_blank');
  };
  const handleEditStore = (data: {
    storeName: string;
    organizationId: string;
    supportEmail: string;
    supportPhone: string;
  }) => {
    showToast('Store details updated successfully', 'success');
    // In a real app, this would update the store via API
  };
  const handleDuplicateStore = (newStoreName: string) => {
    showToast(`Store duplicated as "${newStoreName}"`, 'success');
    // In a real app, this would create a duplicate via API and navigate to it
    setTimeout(() => {
      navigate('/pop-up-stores');
    }, 1500);
  };
  const handleForceClose = () => {
    showToast(`${store.name} has been force closed`, 'success');
    // In a real app, this would update the store status via API
  };
  return <div className="space-y-6" data-id="element-1024">
      <div className="flex items-center space-x-4 mb-4" data-id="element-1025">
        <Link to="/pop-up-stores" className="text-gray-500 hover:text-gray-700" data-id="element-1026">
          <ArrowLeft className="h-5 w-5" data-id="element-1027" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1028">{store.name}</h1>
        <Badge variant={getStatusColor(status)} className="text-sm" data-id="element-1029">
          {status.toUpperCase()}
        </Badge>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1030" />

      <div className="mt-6" data-id="element-1031">
        {activeTab === 'overview' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1032">
            <div className="md:col-span-2 space-y-6" data-id="element-1033">
              <Card title="Store Performance" data-id="element-1034">
                <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-1035">
                  <div className="px-4 py-5 bg-gray-50 shadow rounded-lg overflow-hidden sm:p-6" data-id="element-1036">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1037">
                      Total Orders
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900" data-id="element-1038">
                      {store.stats.ordersCount}
                    </dd>
                  </div>
                  <div className="px-4 py-5 bg-gray-50 shadow rounded-lg overflow-hidden sm:p-6" data-id="element-1039">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1040">
                      Total Revenue
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900" data-id="element-1041">
                      ${store.stats.revenue.toFixed(2)}
                    </dd>
                  </div>
                  <div className="px-4 py-5 bg-gray-50 shadow rounded-lg overflow-hidden sm:p-6" data-id="element-1042">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1043">
                      Conversion Rate
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900" data-id="element-1044">
                      4.2%
                    </dd>
                  </div>
                </dl>
              </Card>

              <Card title="Timeline" data-id="element-1045">
                <div className="flow-root" data-id="element-1046">
                  <ul role="list" className="-mb-8" data-id="element-1047">
                    <li className="relative pb-8" data-id="element-1048">
                      <div className="relative flex space-x-3" data-id="element-1049">
                        <div data-id="element-1050">
                          <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white" data-id="element-1051">
                            <ExternalLink className="h-5 w-5 text-white" aria-hidden="true" data-id="element-1052" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4" data-id="element-1053">
                          <div data-id="element-1054">
                            <p className="text-sm text-gray-500" data-id="element-1055">
                              Store opened automatically
                            </p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500" data-id="element-1056">
                            <time dateTime="2020-09-20" data-id="element-1057">Sep 20</time>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </Card>
            </div>

            <div className="space-y-6" data-id="element-1058">
              <Card title="Public Storefront" data-id="element-1059">
                <div className="space-y-4" data-id="element-1060">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-id="element-1061">
                    <span className="text-sm font-medium text-gray-700" data-id="element-1062">
                      Published
                    </span>
                    <Badge variant={store.settings.isPublished ? 'green' : 'gray'} data-id="element-1063">
                      {store.settings.isPublished ? 'ON' : 'OFF'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-id="element-1064">
                    <span className="text-sm font-medium text-gray-700" data-id="element-1065">
                      Ordering
                    </span>
                    <Badge variant={store.settings.orderingEnabled ? 'green' : 'gray'} data-id="element-1066">
                      {store.settings.orderingEnabled ? 'ON' : 'OFF'}
                    </Badge>
                  </div>

                  <div data-id="element-1067">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1068">
                      Storefront URL
                    </label>
                    <div className="p-3 bg-gray-50 rounded-md border border-gray-200 break-all text-sm text-gray-600 font-mono" data-id="element-1069">
                      {storefrontUrl}
                    </div>
                  </div>

                  <div data-id="element-1070">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1071">
                      Preview State
                    </label>
                    <Select value={previewState} onChange={e => setPreviewState(e.target.value)} options={[{
                  value: 'actual',
                  label: 'Actual State'
                }, {
                  value: 'coming-soon',
                  label: 'Force: Coming Soon'
                }, {
                  value: 'open',
                  label: 'Force: Open'
                }, {
                  value: 'closed',
                  label: 'Force: Closed'
                }]} data-id="element-1072" />
                  </div>

                  <div className="flex space-x-2" data-id="element-1073">
                    <Button className="flex-1" variant="outline" onClick={handleCopyLink} data-id="element-1074">
                      {copied ? <>
                          <Check className="h-4 w-4 mr-2" data-id="element-1075" />
                          Copied!
                        </> : <>
                          <Copy className="h-4 w-4 mr-2" data-id="element-1076" />
                          Copy Link
                        </>}
                    </Button>
                    <Button className="flex-1" variant="secondary" onClick={handlePreview} data-id="element-1077">
                      <ExternalLink className="h-4 w-4 mr-2" data-id="element-1078" />
                      Preview
                    </Button>
                  </div>

                  {previewState !== 'actual' && <p className="text-xs text-gray-500 italic" data-id="element-1079">
                      Preview will show the "{previewState.replace('-', ' ')}"
                      state regardless of actual schedule
                    </p>}
                </div>
              </Card>

              <Card title="Quick Actions" data-id="element-1080">
                <div className="space-y-2" data-id="element-1081">
                  <Button className="w-full" variant="outline" onClick={() => setIsEditModalOpen(true)} data-id="element-1082">
                    Edit Store Details
                  </Button>
                  <Button className="w-full" variant="outline" onClick={() => setIsDuplicateModalOpen(true)} data-id="element-1083">
                    Duplicate Store
                  </Button>
                  <Button className="w-full" variant="danger" onClick={() => setIsForceCloseModalOpen(true)} data-id="element-1084">
                    Force Close Store
                  </Button>
                </div>
              </Card>
            </div>
          </div>}

        {activeTab === 'schedule' && <WizardStep2Schedule data-id="element-1085" />}
        {activeTab === 'products' && <WizardStep4Products data-id="element-1086" />}
        {activeTab === 'orders' && <div className="text-center py-12 bg-white rounded-lg border border-gray-200" data-id="element-1087">
            <p className="text-gray-500" data-id="element-1088">
              Orders table would be displayed here (reusing Orders page
              component).
            </p>
            <Link to="/orders" className="text-red-600 hover:text-red-700 font-medium mt-2 inline-block" data-id="element-1089">
              View All Orders
            </Link>
          </div>}
        {activeTab === 'fulfillment' && <Card title="Fulfillment & Exports" data-id="element-1090">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1091">
              <Button variant="outline" data-id="element-1092">Download Production Summary</Button>
              <Button variant="outline" data-id="element-1093">Download Decoration Report</Button>
              <Button variant="outline" data-id="element-1094">Download Personalization Roster</Button>
              <Button variant="outline" data-id="element-1095">Download Shipping List</Button>
            </div>
          </Card>}
        {activeTab === 'settings' && <div className="space-y-6" data-id="element-1096">
            <Card title="Store Settings" data-id="element-1097">
              <div className="space-y-4" data-id="element-1098">
                <div data-id="element-1099">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1100">
                    URL Slug
                  </label>
                  <input type="text" value={store.slug} disabled={store.settings.isPublished} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-1101" />
                  {store.settings.isPublished && <p className="mt-1 text-xs text-gray-500" data-id="element-1102">
                      Slug cannot be changed after store is published
                    </p>}
                </div>
              </div>
            </Card>

            <Card title="Danger Zone" data-id="element-1103">
              <p className="text-sm text-gray-500 mb-4" data-id="element-1104">
                Once you delete a store, there is no going back. Please be
                certain.
              </p>
              <Button variant="danger" data-id="element-1105">Delete Store</Button>
            </Card>
          </div>}
      </div>

      {/* Modals */}
      <EditStoreDetailsModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} storeName={store.name} organizationId={store.organizationId} supportEmail={store.supportEmail} supportPhone={store.supportPhone || ''} onSave={handleEditStore} data-id="element-1106" />

      <DuplicateStoreModal isOpen={isDuplicateModalOpen} onClose={() => setIsDuplicateModalOpen(false)} storeName={store.name} onDuplicate={handleDuplicateStore} data-id="element-1107" />

      <ForceCloseStoreModal isOpen={isForceCloseModalOpen} onClose={() => setIsForceCloseModalOpen(false)} storeName={store.name} onConfirm={handleForceClose} data-id="element-1108" />
    </div>;
}